import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  IconButton,
  Divider,
  Paper,
  LinearProgress,
  Alert,
  Avatar,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack,
  Phone,
  WhatsApp,
  Email,
  LocationOn,
  AccountBalance,
  Person,
  CreditCard,
  Business,
  Work,
  CalendarToday,
  Assessment,
  Security,
  Tag,
  Edit,
  Refresh,
  Savings,
  History,
  TaskAlt,
  ForwardToInbox,
} from '@mui/icons-material';
import { getBorrowerById } from '../../services/request/Borrowers';
import {
  listDepositsByBorrower,
  createDeposit,
  closeDeposit,
  notifyDepositByEmail,
  getDepositAuditTrail,
  CustomerDepositDTO,
  DepositAuditEntry,
} from '../../services/request/Deposits';
import { BorrowerAPIItem } from '../../models/LendingModels';
import { formatCurrency } from '../../utils/CurrencyFormat';
import { PageHeader } from '../../shared-components/ui';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CustomerDetailsView: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [borrower, setBorrower] = useState<BorrowerAPIItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deposits, setDeposits] = useState<CustomerDepositDTO[]>([]);
  const [loadingDeposits, setLoadingDeposits] = useState(false);
  const [openDepositDialog, setOpenDepositDialog] = useState(false);
  const [depositForm, setDepositForm] = useState({
    amount: '',
    depositDate: new Date().toISOString().slice(0, 10),
    maturityDate: '',
    referenceNumber: '',
    source: 'BANK_TRANSFER',
    notes: '',
    notifyCustomer: true,
  });
  const [notifyDepositOpen, setNotifyDepositOpen] = useState(false);
  const [depositToNotify, setDepositToNotify] = useState<CustomerDepositDTO | null>(null);
  const [notifyForm, setNotifyForm] = useState({ email: '', message: '' });
  const [sendingNotify, setSendingNotify] = useState(false);
  const [savingDeposit, setSavingDeposit] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditEntries, setAuditEntries] = useState<DepositAuditEntry[]>([]);
  const [auditDepositLabel, setAuditDepositLabel] = useState('');
  const [closeDepositOpen, setCloseDepositOpen] = useState(false);
  const [depositToClose, setDepositToClose] = useState<CustomerDepositDTO | null>(null);
  const [closeForm, setCloseForm] = useState({ paymentMode: 'BANK_TRANSFER', comment: '' });
  const [closingDeposit, setClosingDeposit] = useState(false);

  useEffect(() => {
    if (customerId) {
      fetchBorrowerDetails();
      fetchDeposits();
    }
  }, [customerId]);

  const fetchDeposits = async () => {
    if (!customerId) return;
    try {
      setLoadingDeposits(true);
      const res = await listDepositsByBorrower(customerId);
      const list = res.data ?? (Array.isArray(res) ? res : []);
      setDeposits(Array.isArray(list) ? list : []);
    } catch {
      setDeposits([]);
    } finally {
      setLoadingDeposits(false);
    }
  };

  const totalActiveDeposits = deposits.filter((d) => d.status === 'ACTIVE').reduce((s, d) => s + d.amount, 0);

  const handleSubmitDeposit = async () => {
    if (!customerId) return;
    const amt = Number(depositForm.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    try {
      setSavingDeposit(true);
      await createDeposit(customerId, {
        amount: amt,
        depositDate: depositForm.depositDate,
        maturityDate: depositForm.maturityDate || undefined,
        referenceNumber: depositForm.referenceNumber || undefined,
        source: depositForm.source || undefined,
        notes: depositForm.notes || undefined,
        notifyCustomer: depositForm.notifyCustomer,
      });
      toast.success('Deposit recorded');
      setOpenDepositDialog(false);
      fetchDeposits();
      fetchBorrowerDetails();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save deposit');
    } finally {
      setSavingDeposit(false);
    }
  };

  const openCloseDepositDialog = (d: CustomerDepositDTO) => {
    setDepositToClose(d);
    setCloseForm({ paymentMode: 'BANK_TRANSFER', comment: '' });
    setCloseDepositOpen(true);
  };

  const handleSubmitCloseDeposit = async () => {
    if (!depositToClose) return;
    const c = closeForm.comment.trim();
    if (!c) {
      toast.error('Enter a comment (required to close)');
      return;
    }
    try {
      setClosingDeposit(true);
      await closeDeposit(depositToClose.id, {
        comment: c,
        paymentMode: closeForm.paymentMode,
      });
      toast.success('Deposit closed');
      setCloseDepositOpen(false);
      setDepositToClose(null);
      fetchDeposits();
      fetchBorrowerDetails();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to close deposit';
      toast.error(typeof msg === 'string' ? msg : 'Failed to close deposit');
    } finally {
      setClosingDeposit(false);
    }
  };

  const openNotifyDepositDialog = (d: CustomerDepositDTO) => {
    setDepositToNotify(d);
    setNotifyForm({
      email: borrower?.email?.trim() || '',
      message: '',
    });
    setNotifyDepositOpen(true);
  };

  const handleSendDepositNotify = async () => {
    if (!depositToNotify) return;
    const em = notifyForm.email.trim();
    if (!em) {
      toast.error('Enter recipient email');
      return;
    }
    try {
      setSendingNotify(true);
      await notifyDepositByEmail(depositToNotify.id, {
        recipientEmail: em,
        message: notifyForm.message.trim() || undefined,
      });
      toast.success('Deposit notification sent');
      setNotifyDepositOpen(false);
      setDepositToNotify(null);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to send';
      toast.error(typeof msg === 'string' ? msg : 'Failed to send');
    } finally {
      setSendingNotify(false);
    }
  };

  const openAudit = async (d: CustomerDepositDTO) => {
    setAuditDepositLabel(`${formatCurrency(d.amount)} · ${d.depositDate}`);
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const res = await getDepositAuditTrail(d.id);
      setAuditEntries(res.data ?? []);
    } catch {
      setAuditEntries([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchBorrowerDetails = async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await getBorrowerById(customerId);
      
      if (response) {
        setBorrower(response);
      } else {
        setError('Borrower not found');
      }
    } catch (err: any) {
      setError(err?.response?.status === 404 
        ? 'Borrower not found' 
        : `Failed to fetch borrower details: ${err.message || 'Unknown error'}`);
      console.error('Error fetching borrower:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toUpperCase()) {
      case 'VERIFIED':
      case 'GOOD':
      case 'EXCELLENT':
      case 'LOW':
      case 'PREMIUM':
        return 'success';
      case 'PENDING':
      case 'AVERAGE':
      case 'MEDIUM':
      case 'STANDARD':
        return 'warning';
      case 'REJECTED':
      case 'BAD':
      case 'BLOCKED':
      case 'HIGH':
      case 'BASIC':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const maskSensitiveData = (value?: string, visibleChars: number = 4) => {
    if (!value) return 'N/A';
    if (value.length <= visibleChars) return value;
    return '****' + value.slice(-visibleChars);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading borrower details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchBorrowerDetails}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  if (!borrower) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No borrower data available</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <ToastContainer />
      
      <PageHeader
        eyebrow="Lending · Ops"
        title={borrower.name}
        subtitle={`Borrower ID: ${borrower.id.substring(0, 8)}…`}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={() => navigate('/admin/customers')} aria-label="Back to customers">
              <ArrowBack />
            </IconButton>
            <Button variant="outlined" startIcon={<Refresh />} onClick={fetchBorrowerDetails}>
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => navigate(`/admin/customers/${customerId}/edit`)}
            >
              Edit
            </Button>
          </Stack>
        }
      />

      {/* Highlighted Credit Score */}
      {borrower.creditScore !== undefined && borrower.creditScore !== null && (
        <Box sx={{ mb: 3 }}>
          <Card
            elevation={3}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 2.5,
              py: 1.5,
              borderRadius: 3,
              bgcolor:
                borrower.creditScore >= 4
                  ? 'success.light'
                  : borrower.creditScore >= 2
                  ? 'warning.light'
                  : 'error.light',
            }}
          >
            <Assessment
              sx={{
                mr: 2,
                color: 'common.white',
              }}
            />
            <Box>
              <Typography
                variant="caption"
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  color: 'common.white',
                }}
              >
                Credit score
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: 'common.white',
                }}
              >
                {borrower.creditScore}
              </Typography>
            </Box>
          </Card>
        </Box>
      )}

      {/* Status Chips */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {borrower.creditStatus && (
          <Chip
            label={`Credit: ${borrower.creditStatus}`}
            color={getStatusColor(borrower.creditStatus)}
            size="small"
          />
        )}
        {borrower.kycStatus && (
          <Chip
            label={`KYC: ${borrower.kycStatus}`}
            color={getStatusColor(borrower.kycStatus)}
            size="small"
          />
        )}
        {borrower.riskCategory && (
          <Chip
            label={`Risk: ${borrower.riskCategory}`}
            color={getStatusColor(borrower.riskCategory)}
            size="small"
          />
        )}
        {borrower.customerSegment && (
          <Chip
            label={`Segment: ${borrower.customerSegment}`}
            color={getStatusColor(borrower.customerSegment)}
            size="small"
          />
        )}
        {borrower.totalOutstanding !== undefined && (
          <Chip
            label={`Outstanding: ${formatCurrency(borrower.totalOutstanding)}`}
            color="primary"
            size="small"
          />
        )}
      </Box>

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Savings color="secondary" />
              <Typography variant="h6" fontWeight="bold">
                Customer deposits
              </Typography>
              <Chip label={`Active total: ${formatCurrency(totalActiveDeposits)}`} color="secondary" size="small" />
            </Box>
            <Button variant="contained" color="secondary" startIcon={<Savings />} onClick={() => setOpenDepositDialog(true)}>
              Record deposit
            </Button>
          </Box>
          {loadingDeposits ? (
            <LinearProgress />
          ) : deposits.length === 0 ? (
            <Typography color="text.secondary">No deposits yet.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Maturity</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Closure</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deposits.map((d) => (
                    <TableRow key={d.id}>
                      <TableCell>{d.depositDate}</TableCell>
                      <TableCell>{d.maturityDate || '—'}</TableCell>
                      <TableCell align="right">{formatCurrency(d.amount)}</TableCell>
                      <TableCell>{d.source || '—'}</TableCell>
                      <TableCell>{d.referenceNumber || '—'}</TableCell>
                      <TableCell>
                        <Chip label={d.status} size="small" color={d.status === 'ACTIVE' ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {d.status === 'CLOSED' ? (
                          <Box>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Paid via {d.closurePaymentMode || '—'}
                            </Typography>
                            <Tooltip title={d.closureComment || ''}>
                              <Typography variant="body2" noWrap>
                                {d.closureComment || '—'}
                              </Typography>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {d.status === 'ACTIVE' && (
                          <Tooltip title="Close deposit">
                            <IconButton size="small" onClick={() => openCloseDepositDialog(d)} color="warning">
                              <TaskAlt fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Email deposit details">
                          <IconButton size="small" onClick={() => openNotifyDepositDialog(d)} color="primary">
                            <ForwardToInbox fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Compliance trail">
                          <IconButton size="small" onClick={() => openAudit(d)}>
                            <History fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Contact Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.contactNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Contact Number
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" />
                      {borrower.contactNumber}
                    </Typography>
                  </Box>
                )}
                {borrower.whatsappNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      WhatsApp Number
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsApp fontSize="small" color="success" />
                      {borrower.whatsappNumber}
                      {borrower.isContactSameAsWhatsapp && (
                        <Chip label="Same as Contact" size="small" color="info" />
                      )}
                    </Typography>
                  </Box>
                )}
                {borrower.email && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" />
                      {borrower.email}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Address Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Address Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.address && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {borrower.address}
                    </Typography>
                  </Box>
                )}
                {(borrower.city || borrower.state || borrower.pincode) && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Location
                    </Typography>
                    <Typography variant="body1">
                      {[borrower.city, borrower.state, borrower.pincode]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CreditCard sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Financial Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.creditScore !== undefined && borrower.creditScore !== null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Credit Score
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="h6"
                        color={
                          borrower.creditScore >= 4
                            ? 'success.main'
                            : borrower.creditScore >= 2
                            ? 'warning.main'
                            : 'error.main'
                        }
                      >
                        {borrower.creditScore}
                      </Typography>
                      <Chip
                        label={
                          borrower.creditScore >= 4
                            ? 'Excellent'
                            : borrower.creditScore >= 2
                            ? 'Good'
                            : 'Needs attention'
                        }
                        size="small"
                        color={
                          borrower.creditScore >= 4
                            ? 'success'
                            : borrower.creditScore >= 2
                            ? 'warning'
                            : 'error'
                        }
                      />
                    </Box>
                  </Box>
                )}
                {borrower.monthlyIncome !== undefined && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Monthly Income
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(borrower.monthlyIncome)}
                    </Typography>
                  </Box>
                )}
                {borrower.totalOutstanding !== undefined && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Outstanding
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="error.main">
                      {formatCurrency(borrower.totalOutstanding)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Person sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Personal Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.dateOfBirth && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Date of Birth
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" />
                      {formatDate(borrower.dateOfBirth)}
                    </Typography>
                  </Box>
                )}
                {borrower.panNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      PAN Number
                    </Typography>
                    <Typography variant="body1">
                      {maskSensitiveData(borrower.panNumber, 4)}
                    </Typography>
                  </Box>
                )}
                {borrower.aadhaarNumber && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Aadhaar Number
                    </Typography>
                    <Typography variant="body1">
                      {maskSensitiveData(borrower.aadhaarNumber, 4)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Employment Information */}
        {(borrower.employmentStatus || borrower.occupation || borrower.companyName) && (
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Work sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Employment Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {borrower.employmentStatus && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Employment Status
                      </Typography>
                      <Chip
                        label={borrower.employmentStatus.replace('_', ' ')}
                        color={getStatusColor(borrower.employmentStatus)}
                        size="small"
                      />
                    </Box>
                  )}
                  {borrower.occupation && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Occupation
                      </Typography>
                      <Typography variant="body1">{borrower.occupation}</Typography>
                    </Box>
                  )}
                  {borrower.companyName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Company Name
                      </Typography>
                      <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business fontSize="small" />
                        {borrower.companyName}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Bank Information */}
        {(borrower.bankAccountNumber || borrower.ifscCode || borrower.bankName) && (
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Bank Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {borrower.bankAccountNumber && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Account Number
                      </Typography>
                      <Typography variant="body1">
                        {maskSensitiveData(borrower.bankAccountNumber, 4)}
                      </Typography>
                    </Box>
                  )}
                  {borrower.ifscCode && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        IFSC Code
                      </Typography>
                      <Typography variant="body1">{borrower.ifscCode}</Typography>
                    </Box>
                  )}
                  {borrower.bankName && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Bank Name
                      </Typography>
                      <Typography variant="body1">{borrower.bankName}</Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* CRM Information */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Assessment sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  CRM Information
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {borrower.referralCode && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Referral Code
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {borrower.referralCode}
                    </Typography>
                  </Box>
                )}
                {borrower.sourceOfLead && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Source of Lead
                    </Typography>
                    <Chip
                      label={borrower.sourceOfLead.replace('_', ' ')}
                      size="small"
                      color="primary"
                    />
                  </Box>
                )}
                {borrower.lastContactDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Last Contact Date
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" />
                      {formatDate(borrower.lastContactDate)}
                    </Typography>
                  </Box>
                )}
                {borrower.nextFollowupDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Next Follow-up Date
                    </Typography>
                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" />
                      {formatDate(borrower.nextFollowupDate)}
                    </Typography>
                  </Box>
                )}
                {borrower.tags && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {borrower.tags.split(',').map((tag, index) => (
                        <Chip
                          key={index}
                          label={tag.trim()}
                          size="small"
                          icon={<Tag />}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Information */}
        {borrower.comments && (
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Security sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight="bold">
                    Additional Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1">{borrower.comments}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/admin/customers/${customerId}/debits`)}
        >
          View Debits
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate(`/admin/customers/${customerId}/debits`)}
        >
          Manage Loans
        </Button>
      </Box>

      <Dialog
        open={closeDepositOpen}
        onClose={() => !closingDeposit && setCloseDepositOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Close deposit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
            Record how the deposit was settled and a comment for the audit trail. Only active deposits can be closed.
          </Typography>
          {depositToClose && (
            <Typography variant="subtitle2" gutterBottom>
              {formatCurrency(depositToClose.amount)} · {depositToClose.depositDate}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Settlement payment mode</InputLabel>
            <Select
              value={closeForm.paymentMode}
              label="Settlement payment mode"
              onChange={(e) => setCloseForm({ ...closeForm, paymentMode: e.target.value })}
            >
              <MenuItem value="BANK_TRANSFER">Bank transfer</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Comment (required)"
            placeholder="Reason / reference for closure…"
            multiline
            minRows={3}
            value={closeForm.comment}
            onChange={(e) => setCloseForm({ ...closeForm, comment: e.target.value })}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDepositOpen(false)} disabled={closingDeposit}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" onClick={handleSubmitCloseDeposit} disabled={closingDeposit}>
            {closingDeposit ? 'Closing…' : 'Close deposit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDepositDialog} onClose={() => setOpenDepositDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record deposit</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Amount (INR)"
            type="number"
            value={depositForm.amount}
            onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            label="Deposit date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={depositForm.depositDate}
            onChange={(e) => setDepositForm({ ...depositForm, depositDate: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Maturity date (optional)"
            type="date"
            InputLabelProps={{ shrink: true }}
            helperText="Used for automated expiry reminders in the daily batch"
            value={depositForm.maturityDate}
            onChange={(e) => setDepositForm({ ...depositForm, maturityDate: e.target.value })}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={depositForm.source}
              label="Source"
              onChange={(e) => setDepositForm({ ...depositForm, source: e.target.value })}
            >
              <MenuItem value="BANK_TRANSFER">Bank transfer</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Reference"
            value={depositForm.referenceNumber}
            onChange={(e) => setDepositForm({ ...depositForm, referenceNumber: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Notes"
            multiline
            minRows={2}
            value={depositForm.notes}
            onChange={(e) => setDepositForm({ ...depositForm, notes: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={depositForm.notifyCustomer}
                onChange={(e) => setDepositForm({ ...depositForm, notifyCustomer: e.target.checked })}
              />
            }
            label="Email customer if profile has an email (deposit confirmation)"
            sx={{ mt: 1, alignItems: 'flex-start' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDepositDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitDeposit} disabled={savingDeposit}>
            {savingDeposit ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={notifyDepositOpen} onClose={() => !sendingNotify && setNotifyDepositOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Notify about deposit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
            Sends the same deposit summary email to any address. Optional message appears in the email. Action is logged in
            the audit trail.
          </Typography>
          {depositToNotify && (
            <Typography variant="subtitle2" gutterBottom>
              {formatCurrency(depositToNotify.amount)} · {depositToNotify.depositDate}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Recipient email"
            type="email"
            value={notifyForm.email}
            onChange={(e) => setNotifyForm({ ...notifyForm, email: e.target.value })}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            label="Custom message (optional)"
            multiline
            minRows={3}
            value={notifyForm.message}
            onChange={(e) => setNotifyForm({ ...notifyForm, message: e.target.value })}
            placeholder="e.g. Please retain this for your records…"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDepositOpen(false)} disabled={sendingNotify}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<ForwardToInbox />} onClick={handleSendDepositNotify} disabled={sendingNotify}>
            {sendingNotify ? 'Sending…' : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Deposit audit trail — {auditDepositLabel}</DialogTitle>
        <DialogContent>
          {auditLoading ? (
            <LinearProgress />
          ) : auditEntries.length === 0 ? (
            <Typography color="text.secondary">No audit entries.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditEntries.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      {a.performedAt ? new Date(a.performedAt).toLocaleString('en-IN') : '—'}
                    </TableCell>
                    <TableCell>{a.action}</TableCell>
                    <TableCell>{a.summary || '—'}</TableCell>
                    <TableCell>{a.performedBy || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerDetailsView;
